import { computed, readonly, ref } from 'vue';
import {
  config,
  type AppConfig,
  isDevelopment,
  isStaging,
  isProduction,
  logger,
} from '@/common/services/config';

/**
 * Composable pour accéder à la configuration de l'application
 * Fournit un accès réactif aux variables d'environnement
 */
export function useAppConfig() {
  // Configuration réactive (en lecture seule)
  const appConfig = readonly(ref<AppConfig>(config));

  // Propriétés calculées pour un accès facile
  const mode = computed(() => appConfig.value.mode);
  const isDevMode = computed(() => isDevelopment());
  const isStagingMode = computed(() => isStaging());
  const isProdMode = computed(() => isProduction());

  // Configuration API
  const apiConfig = computed(() => appConfig.value.api);
  const msalConfig = computed(() => appConfig.value.msal);
  const featuresConfig = computed(() => appConfig.value.features);
  const aurionConfig = computed(() => appConfig.value.aurion);

  // Fonctions utilitaires
  const getApiUrl = (endpoint: string) => {
    const baseUrl = appConfig.value.api.baseUrl;
    return `${baseUrl}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
  };

  const getLegacyApiUrl = (endpoint: string) => {
    const legacyUrl = appConfig.value.api.baseUrlLegacy;
    logger.log('Legacy URL:', legacyUrl);
    return `${legacyUrl}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
  };

  const getAurionUrl = (parameters?: Record<string, string | number>) => {
    let urlTemplate = appConfig.value.aurion.urlTemplate;

    // Formatage du template avec les paramètres fournis
    if (parameters) {
      Object.entries(parameters).forEach(([key, value]) => {
        urlTemplate = urlTemplate.replace(new RegExp(`{${key}}`, 'g'), String(value));
      });
    }

    return `${urlTemplate}`;
  };

  // Logger conditionnel
  const log = {
    info: (...args: any[]) => logger.log('[INFO]', ...args),
    warn: (...args: any[]) => logger.warn('[WARN]', ...args),
    error: (...args: any[]) => logger.error('[ERROR]', ...args),
    debug: (...args: any[]) => logger.debug('[DEBUG]', ...args),
  };

  return {
    // Configuration
    config: appConfig,
    mode,
    isDevMode,
    isStagingMode,
    isProdMode,

    // Sections de configuration
    apiConfig,
    msalConfig,
    featuresConfig,
    aurionConfig,

    // Utilitaires
    getApiUrl,
    getLegacyApiUrl,
    getAurionUrl,
    log,

    // Accès direct aux propriétés principales
    appName: computed(() => appConfig.value.app.name),
    appVersion: computed(() => appConfig.value.app.version),
    baseUrl: computed(() => appConfig.value.app.baseUrl),
  };
}
